import { useCallback, useEffect, useRef, useState } from 'react';
export function usePlanningData(projectId, filters = {}) {
    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState(null);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const syncIdRef = useRef(null);
    const pollIntervalRef = useRef(null);
    const buildUrl = useCallback(() => {
        const params = new URLSearchParams();
        if (filters.versions?.length)
            params.set('versions', filters.versions.join(','));
        if (filters.epics?.length)
            params.set('epics', filters.epics.join(','));
        if (filters.assignees?.length)
            params.set('assignees', filters.assignees.join(','));
        if (filters.statuses?.length)
            params.set('statuses', filters.statuses.join(','));
        if (filters.sprints?.length)
            params.set('sprints', filters.sprints.join(','));
        if (filters.search)
            params.set('search', filters.search);
        const qs = params.toString();
        return `/api/planning/${projectId}/tree${qs ? `?${qs}` : ''}`;
    }, [projectId, filters.versions, filters.epics, filters.assignees, filters.statuses, filters.sprints, filters.search]);
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(buildUrl(), { credentials: 'include' });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || `Request failed (${response.status})`);
            }
            const data = await response.json();
            setTree(data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
        finally {
            setLoading(false);
        }
    }, [buildUrl]);
    useEffect(() => {
        if (projectId) {
            void fetchData();
        }
    }, [projectId, fetchData]);
    // Clean up polling on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, []);
    const pollProgress = useCallback(async (syncId) => {
        try {
            const response = await fetch(`/api/planning/${projectId}/sync-progress?syncId=${syncId}`, {
                credentials: 'include',
            });
            if (!response.ok)
                return;
            const data = await response.json();
            setSyncProgress(data);
            if (data.status === 'completed' || data.status === 'failed') {
                // Stop polling
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
                setSyncing(false);
                syncIdRef.current = null;
                // Refetch the tree if sync completed
                if (data.status === 'completed') {
                    void fetchData();
                }
            }
        }
        catch {
            // Ignore poll errors
        }
    }, [projectId, fetchData]);
    const startSync = useCallback(async () => {
        if (syncing) {
            // Just show the modal if already syncing
            setShowSyncModal(true);
            return;
        }
        setSyncing(true);
        setShowSyncModal(true);
        try {
            const response = await fetch(`/api/planning/${projectId}/sync`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || 'Failed to start sync');
            }
            const data = await response.json();
            const syncId = data.syncId;
            syncIdRef.current = syncId;
            // Set initial progress state
            setSyncProgress({
                syncId,
                projectId,
                status: 'running',
                step: 'Initializing',
                steps: ['Sync versions & epics', 'Sync issues', 'Link issues to epics', 'Complete'],
                completedSteps: [],
                log: [],
                startedAt: new Date().toISOString(),
            });
            // Start polling every 1.5 seconds
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
            pollIntervalRef.current = setInterval(() => {
                void pollProgress(syncId);
            }, 1500);
            // Also poll immediately
            void pollProgress(syncId);
        }
        catch (err) {
            setSyncing(false);
            setSyncProgress({
                syncId: '',
                projectId,
                status: 'failed',
                step: 'Failed',
                steps: [],
                completedSteps: [],
                log: [{ timestamp: new Date().toISOString(), level: 'error', message: err instanceof Error ? err.message : 'Failed to start sync' }],
                error: err instanceof Error ? err.message : 'Failed to start sync',
                startedAt: new Date().toISOString(),
                completedAt: new Date().toISOString(),
            });
        }
    }, [syncing, projectId, pollProgress]);
    const openSyncModal = useCallback(() => {
        setShowSyncModal(true);
    }, []);
    const closeSyncModal = useCallback(() => {
        setShowSyncModal(false);
    }, []);
    const reorder = useCallback(async (items) => {
        const response = await fetch(`/api/planning/${projectId}/reorder`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ items }),
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to reorder');
        }
    }, [projectId]);
    const moveToEpic = useCallback(async (issueKey, newEpicKey, oldEpicKey) => {
        const response = await fetch(`/api/planning/${projectId}/move-to-epic`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ issueKey, newEpicKey, oldEpicKey }),
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to move issue');
        }
        const data = await response.json();
        return data.requestId;
    }, [projectId]);
    const updateField = useCallback(async (issueKey, fieldName, oldValue, newValue) => {
        const response = await fetch(`/api/planning/${projectId}/update-field`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ issueKey, fieldName, oldValue, newValue }),
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to update field');
        }
        const data = await response.json();
        return data.requestId;
    }, [projectId]);
    const createIssue = useCallback(async (issueType, summary, parentKey, epicKey, additionalFields) => {
        const response = await fetch(`/api/planning/${projectId}/create-issue`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ issueType, summary, parentKey, epicKey, additionalFields }),
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to create issue');
        }
        const data = await response.json();
        return data.requestId;
    }, [projectId]);
    const bulkMoveToEpic = useCallback(async (issueKeys, newEpicKey, oldEpicKeys) => {
        const response = await fetch(`/api/planning/${projectId}/bulk-move-to-epic`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ issueKeys, newEpicKey, oldEpicKeys }),
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to bulk move issues');
        }
        return await response.json();
    }, [projectId]);
    return {
        tree,
        loading,
        error,
        syncing,
        syncProgress,
        showSyncModal,
        refetch: fetchData,
        startSync,
        openSyncModal,
        closeSyncModal,
        reorder,
        moveToEpic,
        bulkMoveToEpic,
        updateField,
        createIssue,
    };
}
//# sourceMappingURL=usePlanningData.js.map